// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show, createSignal } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import type { GroupBody, ProjectBody, ProjectGroupBody } from "./api/generated";
import {
  assignProjectGroup,
  createProject,
  deleteProject,
  listGroups,
  listProjects,
  listProjectGroups,
  removeProjectGroup,
  updateProject,
} from "./api/generated";
import { apiErrorMessage, authSlotHeaders } from "./api/runtime";
import { Button, Field, Form } from "./components/forms";
import { Heading } from "./components/layout";
import { AccessNotPermitted, useEntitlement } from "./entitlements";
import {
  projectCreateFormSchema,
  projectUpdateFormSchema,
} from "./form-validation";
import { AppLink, useGo } from "./navigation";

const roles = [
  ["admins", "Admin"],
  ["managers", "Manager"],
  ["operators", "Operator"],
  ["editors", "Editor"],
  ["viewers", "Viewer"],
] as const;

type Props = { slot: number; account: string; rest: string[] };

function ProjectGroups(p: Props & { project: ProjectBody }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = createSignal("");
  const [message, setMessage] = createSignal("");
  const [error, setError] = createSignal("");
  const groups = createQuery(() => ({
    queryKey: [p.slot, p.account, "groups"],
    queryFn: async () =>
      (
        await listGroups({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
  }));
  const assignments = createQuery(() => ({
    queryKey: [p.slot, p.account, "projects", p.project.id, "groups"],
    queryFn: async () =>
      (
        await listProjectGroups({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account, id: p.project.id },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
  }));
  const assignment = (group: GroupBody): ProjectGroupBody | undefined =>
    assignments.data?.find((item) => item.id === group.id);
  const save = async (group: GroupBody, role: string) => {
    setBusy(group.id);
    setError("");
    setMessage("");
    try {
      const path = { account: p.account, id: p.project.id, group: group.id };
      if (role) {
        await assignProjectGroup({
          throwOnError: true,
          responseStyle: "fields",
          path,
          headers: authSlotHeaders(p.slot),
          body: { clusterRole: role as ProjectGroupBody["clusterRole"] },
        });
        setMessage(
          `${group.name} assigned as ${roles.find(([value]) => value === role)?.[1]}.`,
        );
      } else {
        await removeProjectGroup({
          throwOnError: true,
          responseStyle: "fields",
          path,
          headers: authSlotHeaders(p.slot),
        });
        setMessage(`${group.name} unassigned.`);
      }
      await queryClient.invalidateQueries({
        queryKey: [p.slot, p.account, "projects", p.project.id, "groups"],
      });
      await queryClient.invalidateQueries({
        queryKey: [p.slot, p.account, "groups", group.id, "projects"],
      });
    } catch (reason) {
      setError(apiErrorMessage(reason));
    } finally {
      setBusy("");
    }
  };
  return (
    <section
      class="mt-8 grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4"
      aria-labelledby="project-groups-heading"
    >
      <div>
        <h2 id="project-groups-heading" class="text-xl font-bold">
          Project groups
        </h2>
        <p class="mt-1 text-sm text-slate-500">
          Assign each group a cluster role, or leave it unassigned.
        </p>
      </div>
      <Show when={groups.isPending || assignments.isPending}>
        <p>Loading project groups…</p>
      </Show>
      <Show when={groups.isError || assignments.isError}>
        <div role="alert" class="alert alert-error">
          Unable to load project groups.
        </div>
      </Show>
      <Show when={groups.isSuccess && assignments.isSuccess}>
        <div class="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3">
          <For each={groups.data}>
            {(group) => {
              let select!: HTMLSelectElement;
              return (
                <article class="panel flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
                  <div class="min-w-0 flex-1">
                    <h3 class="truncate font-semibold">
                      <AppLink
                        class="text-primary hover:underline"
                        to={`/u${p.slot}/${p.account}/groups/${group.id}/edit`}
                      >
                        {group.name}
                      </AppLink>
                    </h3>
                    <p class="text-sm text-slate-500">
                      Current:{" "}
                      {roles.find(
                        ([value]) => value === assignment(group)?.clusterRole,
                      )?.[1] ?? "Unassigned"}
                    </p>
                  </div>
                  <label class="sr-only" for={`project-role-${group.id}`}>
                    Cluster role for {group.name}
                  </label>
                  <select
                    ref={select}
                    id={`project-role-${group.id}`}
                    class="select select-bordered w-full sm:w-44"
                    value={assignment(group)?.clusterRole ?? ""}
                  >
                    <option value="">Unassigned</option>
                    <For each={roles}>
                      {([value, label]) => (
                        <option value={value}>{label}</option>
                      )}
                    </For>
                  </select>
                  <button
                    class="btn btn-secondary"
                    type="button"
                    disabled={busy() === group.id}
                    onClick={() => save(group, select.value)}
                  >
                    {busy() === group.id ? "Saving…" : "Update role"}
                  </button>
                </article>
              );
            }}
          </For>
        </div>
      </Show>
      <Show when={error()}>
        <div role="alert" class="alert alert-error">
          {error()}
        </div>
      </Show>
      <Show when={message()}>
        <div role="status" class="alert alert-success">
          {message()}
        </div>
      </Show>
    </section>
  );
}

export function ProjectsPage(p: Props) {
  const go = useGo();
  const queryClient = useQueryClient();
  const canManage = useEntitlement("account.project.manage");
  const [error, setError] = createSignal("");
  const projects = createQuery(() => ({
    queryKey: [p.slot, p.account, "projects"],
    queryFn: async () =>
      (
        await listProjects({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
  }));
  const base = `/u${p.slot}/${p.account}/projects`;
  const current = () => projects.data?.find((item) => item.id === p.rest[1]);
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: [p.slot, p.account, "projects"],
    });
  if (p.rest[1] && !canManage())
    return (
      <>
        <Heading title={p.rest[1] === "new" ? "Add project" : "Edit project"} />
        <AccessNotPermitted />
      </>
    );
  if (p.rest[1] === "new")
    return (
      <>
        <Heading title="Add project" />
        <section class="panel max-w-2xl p-6">
          <Form
            schema={projectCreateFormSchema}
            submitLabel="Create project"
            action={async (data) => {
              await createProject({
                throwOnError: true,
                responseStyle: "fields",
                path: { account: p.account },
                headers: authSlotHeaders(p.slot),
                body: { name: data.name ?? "" },
              });
              await refresh();
              go(base);
            }}
          >
            <Field
              name="name"
              label="Project name"
              required
              minLength={1}
              maxLength={120}
            />
          </Form>
        </section>
      </>
    );
  if (p.rest[1])
    return (
      <>
        <Heading title="Edit project" />
        <Show when={projects.isPending}>
          <p>Loading project…</p>
        </Show>
        <Show when={projects.isError}>
          <div role="alert" class="alert alert-error">
            Unable to load project.
          </div>
        </Show>
        <Show when={projects.isSuccess && !current()}>
          <div role="alert" class="alert alert-error">
            Project not found.
          </div>
        </Show>
        <Show when={current()} keyed>
          {(project) => (
            <>
              <section class="panel max-w-2xl p-6">
                <Form
                  schema={projectUpdateFormSchema}
                  submitLabel="Save project"
                  successMessage="Project renamed."
                  action={async (data) => {
                    const name = data.name ?? "";
                    await updateProject({
                      throwOnError: true,
                      responseStyle: "fields",
                      path: { account: p.account, id: project.id },
                      headers: authSlotHeaders(p.slot),
                      body: { name },
                    });
                    await queryClient.invalidateQueries({
                      queryKey: [p.slot, p.account, "projects"],
                      exact: true,
                      refetchType: "none",
                    });
                  }}
                >
                  <Field
                    name="name"
                    label="Project name"
                    value={project.name}
                    required
                    minLength={1}
                    maxLength={120}
                  />
                </Form>
              </section>
              <ProjectGroups {...p} project={project} />
            </>
          )}
        </Show>
      </>
    );
  const remove = async (project: ProjectBody) => {
    if (!confirm(`Delete project “${project.name}”?`)) return;
    setError("");
    try {
      await deleteProject({
        throwOnError: true,
        responseStyle: "fields",
        path: { account: p.account, id: project.id },
        headers: authSlotHeaders(p.slot),
      });
      await refresh();
    } catch (reason) {
      setError(apiErrorMessage(reason));
    }
  };
  return (
    <>
      <Heading
        title="Projects"
        copy="Organise clusters and control which groups can access them."
        action={
          <Show when={canManage()}>
            <Button onClick={() => go(`${base}/new`)}>Add new</Button>
          </Show>
        }
      />
      <Show when={error()}>
        <div role="alert" class="alert alert-error mb-4">
          {error()}
        </div>
      </Show>
      <Show when={projects.isPending}>
        <p>Loading projects…</p>
      </Show>
      <Show when={projects.isError}>
        <div role="alert" class="alert alert-error">
          Unable to load projects.
        </div>
      </Show>
      <Show when={projects.isSuccess}>
        <div class="grid gap-3">
          <For each={projects.data}>
            {(project) => (
              <article class="panel flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
                <h2 class="min-w-0 flex-1 truncate font-semibold">
                  {project.name}
                </h2>
                <Show when={canManage()}>
                  <div class="flex flex-wrap gap-2">
                    <AppLink
                      class="btn btn-ghost text-base"
                      to={`${base}/${project.id}/edit`}
                    >
                      Edit
                    </AppLink>
                    <button
                      class="btn btn-ghost text-base text-error"
                      type="button"
                      onClick={() => remove(project)}
                    >
                      Delete
                    </button>
                  </div>
                </Show>
              </article>
            )}
          </For>
        </div>
      </Show>
    </>
  );
}
